import { makeStyles } from '@material-ui/styles'
import { setActiveRegion } from 'api-client/helpers'
import { RootState } from 'app/store'
import { getTrackingConfig, updateTrackingConfig } from 'app/utils/tracking'
import Avatar from 'core/components/Avatar'
import SubmitButton from 'core/components/buttons/SubmitButton'
import DocumentMeta from 'core/components/DocumentMeta'
import Progress from 'core/components/progress/Progress'
import { ErrorMessage } from 'core/components/validatedForm/ErrorMessage'
import { FormFieldCard } from 'core/components/validatedForm/FormFieldCard'
import TextField from 'core/components/validatedForm/TextField'
import ToggleSwitchField from 'core/components/validatedForm/ToggleSwitchField'
import ValidatedForm from 'core/components/validatedForm/ValidatedForm'
import Text from 'core/elements/Text'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { sessionActions, SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import useScopedPreferences from 'core/session/useScopedPreferences'
import Theme from 'core/themes/model'
import { emailValidator } from 'core/utils/fieldValidators'
import { isAdminRole, isSelfServiceRole } from 'k8s/util/helpers'
import { prop } from 'ramda'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { updateUserPassword } from '../userManagement/users/actions'
import { updateUser } from '../userManagement/users/new-actions'
import UserPasswordField from '../userManagement/users/UserPasswordField'
import TenantsAndRoleAccessTable from './tenants-and-role-access-table'

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'grid',
    gridGap: theme.spacing(4),
    gridTemplateColumns: '766px 766px',
    '@media (max-width:1920px)': {
      gridTemplateColumns: 'unset',
      maxWidth: 766,
      gridGap: 16,
      '& .current-plan-card': {
        order: -1,
      },
    },
  },
  form: {
    marginTop: theme.spacing(2),
  },
  passwordField: {
    '& .input[type=password]::-ms-reveal, & .input[type=password]::-ms-clear': {
      display: 'none',
    },
  },
  userDetails: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
  },
  avatar: {
    textAlign: 'center',
    marginTop: theme.spacing(1.5),
  },
  inputField: {
    width: '100% !important',
  },
  button: {
    gridColumn: '2',
    marginTop: theme.spacing(3),
    width: 'max-content',
  },
  passwordForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  confirmPasswordField: {
    gridColumn: '2',
  },
  fields: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
}))

const airgappedSystemUserEmail = 'admin@airctl.localnet'

const MyProfilePage = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { prefs, updatePrefs } = useScopedPreferences()
  const [errorMessage, setErrorMessage] = useState('')
  const [oldUserPrefs, setOldUserPrefs] = useState(prefs)
  const [userUpdated, setUserUpdated] = useState(false)
  const [trackingLoaded, setTrackingLoaded] = useState(false)
  const [initialTracking, setInitialTracking] = useState({
    enableTracking: true,
  })
  const userIdFormFieldSetter = useRef(null)
  const [updatingPassword, setUpdatingPassword] = useState(false)

  const session = useSelector<RootState, SessionState>(prop(sessionStoreKey))
  const { features, userDetails, isSsoToken } = session
  const { id: userId, displayName, email } = userDetails
  const isAdmin = useMemo(() => isAdminRole(session), [session])
  const isSelfService = useMemo(() => isSelfServiceRole(session), [session])

  const { update, updating: updatingUser } = useUpdateAction(updateUser)

  const userInfo = useMemo(
    () => ({
      id: userId,
      username: email,
      displayname: displayName || '',
      email,
    }),
    [displayName, email],
  )

  const loadingSomething = updatingUser || updatingPassword

  useEffect(() => {
    if (isSsoToken || !userIdFormFieldSetter.current) {
      return
    }
    // Reset the initial values for the user ID form with the new updated values
    userIdFormFieldSetter.current.setField('email')(email)
    userIdFormFieldSetter.current.setField('displayName')(displayName)
  }, [displayName, email, isSsoToken])

  useEffect(() => {
    // Whenever the user's email gets updated, transfer their old prefs over to the new email
    if (userUpdated) {
      updatePrefs(oldUserPrefs)
      if (oldUserPrefs.currentRegion) {
        setActiveRegion(oldUserPrefs.currentRegion)
      }
      setUserUpdated(false)
    }
  }, [email])

  useEffect(() => {
    const getTracking = async () => {
      try {
        const trackingConfig = await getTrackingConfig()
        setInitialTracking(trackingConfig)
        setTrackingLoaded(true)
      } catch (e) {
        setInitialTracking({
          enableTracking: true,
        })
        setTrackingLoaded(true)
      }
    }
    getTracking()
  }, [])

  const setupUserIdFieldSetter = (setField) => {
    userIdFormFieldSetter.current = { setField }
  }

  const handleUserIdUpdate = async (values) => {
    // Save the prefs under the old email to transfer to the new email
    setOldUserPrefs(prefs)

    const user = {
      ...userInfo,
      username: values.email || userInfo.email,
      displayname: values.displayname || userInfo.displayname,
    }
    const { success, response: updatedUser } = await update(user)

    if (success) {
      setUserUpdated(true)

      dispatch(
        sessionActions.updateSession({
          username: updatedUser.email,
          userDetails: {
            ...userDetails,
            username: updatedUser.email,
            name: updatedUser.email,
            email: updatedUser.email,
            displayName: updatedUser.displayname, // displayName is a UI variable
            displayname: updatedUser.displayname, // displayname is what we get from the api
          },
        }),
      )
    }
  }

  const handleTrackingUpdate = async (values) => {
    updateTrackingConfig(values)
  }

  const handlePasswordUpdate = async ({ currentPassword, newPassword, confirmedPassword }) => {
    if (newPassword !== confirmedPassword) {
      setErrorMessage('New passwords do not match.')
      return
    }
    setErrorMessage('')
    setUpdatingPassword(true)
    const success = await updateUserPassword({ id: userId, email, currentPassword, newPassword })
    setUpdatingPassword(false)
    if (!success) {
      setErrorMessage('Unable to update password. Current password may be incorrect')
    }
  }

  // Disable change for airgapped system user
  const disableChange = features?.experimental?.airgapped && email === airgappedSystemUserEmail

  return (
    <>
      <DocumentMeta title="User Settings" bodyClasses={['form-view']} />
      <Progress
        message={updatingUser || updatingPassword ? 'Updating user ...' : 'Loading user ...'}
        loading={loadingSomething}
      >
        <div className={classes.root}>
          <div>
            {!isSsoToken && (
              <ValidatedForm
                fullWidth
                initialValues={userInfo}
                onSubmit={handleUserIdUpdate}
                fieldSetter={setupUserIdFieldSetter}
                elevated={false}
              >
                <FormFieldCard>
                  <div className={classes.userDetails}>
                    <div className={classes.fields}>
                      <TextField
                        className={classes.inputField}
                        id="email"
                        label="Email"
                        validations={[emailValidator]}
                        disabled={!isAdmin}
                        readOnly={disableChange}
                      />
                      <TextField
                        className={classes.inputField}
                        id="displayname"
                        label="Display Name"
                        disabled={!isAdmin}
                        readOnly={disableChange}
                      />
                      {isAdmin && !disableChange && (
                        <SubmitButton className={classes.button}>Update</SubmitButton>
                      )}
                    </div>
                    <div className={classes.avatar}>
                      <Avatar displayName={displayName} diameter={120} fontSize={24} />
                    </div>
                  </div>
                </FormFieldCard>
              </ValidatedForm>
            )}
            {!isSsoToken && !disableChange && (
              <ValidatedForm
                classes={{ root: classes.form }}
                onSubmit={handlePasswordUpdate}
                clearOnSubmit
                elevated={false}
              >
                {({ values }) => (
                  <FormFieldCard>
                    <div className={classes.passwordForm}>
                      <UserPasswordField
                        id="currentPassword"
                        label="Current Password"
                        value={values.currentPassword}
                        showPasswordRequirements={false}
                        restrictPassword={false}
                        className={classes.passwordField}
                      />
                      <UserPasswordField
                        id="newPassword"
                        label="New Password"
                        className={classes.passwordField}
                        value={values.newPassword}
                      />
                      <UserPasswordField
                        id="confirmedPassword"
                        label="Confirm New Password"
                        value={values.confirmedPassword}
                        showPasswordRequirements={false}
                        className={classes.passwordField}
                      />
                      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
                      <SubmitButton className={classes.button}>Update</SubmitButton>
                    </div>
                  </FormFieldCard>
                )}
              </ValidatedForm>
            )}
            {/* <FormFieldCard className={classes.form} title="Themes">
              <ThemeChoiceCard />
            </FormFieldCard> */}
            <FormFieldCard className={classes.form} title="Tenants & Role Access">
              <TenantsAndRoleAccessTable userId={userId} />
            </FormFieldCard>
            {/* Todo: Move this to a general settings page down the line */}
            {(isAdmin || isSelfService) && trackingLoaded && (
              <FormFieldCard className={classes.form} title="Product Improvement Program">
                <Text variant="body2">
                  Help us improve the product experience by sharing usage data.
                </Text>
                <ValidatedForm
                  classes={{ root: classes.form }}
                  onSubmit={handleTrackingUpdate}
                  initialValues={initialTracking}
                  elevated={false}
                >
                  <ToggleSwitchField id="enableTracking" label="Share Usage Data" />
                  <SubmitButton className={classes.button}>Update</SubmitButton>
                </ValidatedForm>
              </FormFieldCard>
            )}
          </div>
          {/* {features?.customer_tier !== CustomerTiers.OEM && (
            <div className="current-plan-card">
              <CurrentPlanCard />
            </div>
          )} */}
        </div>
      </Progress>
    </>
  )
}

export default MyProfilePage
